import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { rolesAPI } from '../api/crm'

/**
 * What the signed-in member may do (mirrors the server, which enforces it). Admins get full
 * access. Shapes: scope ALL | TEAM | OWN | NONE; field access EDIT | READ_ONLY | HIDDEN.
 */
export const usePermissions = () => {
  const tenantId = useSelector((state) => state.tenant.current?.tenantId)
  return useQuery({ queryKey: [tenantId, 'permissions'], queryFn: rolesAPI.mine, enabled: !!tenantId, staleTime: 60_000 })
}

const FULL = { view: 'ALL', create: true, edit: 'ALL', delete: 'ALL', fields: {} }

/** Permissions for one record type. While loading, assume full access (the server still decides). */
export const accessFor = (perms, entityType) => {
  const p = !perms || perms.admin ? FULL : perms.permissions.entities?.[entityType] || perms.permissions.defaults || FULL
  const personKeys = (fields) => fields.filter((f) => f.fieldType === 'USER').map((f) => f.fieldKey)
  // OWN = created by me, or I'm in one of its person fields (same rule as the server)
  const isOwn = (record, fields = []) => !!perms && (
    (perms.email && record.createdBy?.toLowerCase() === perms.email.toLowerCase())
    || personKeys(fields).some((k) => record.data?.[k] === perms.userId))
  // TEAM = the same for anyone in my team (me and everyone reporting to me; from the server)
  const isTeams = (record, fields = []) => !!perms && (
    (record.createdBy && (perms.teamEmails || []).includes(record.createdBy.toLowerCase()))
    || personKeys(fields).some((k) => (perms.teamUserIds || []).includes(record.data?.[k])))
  const allows = (scope, record, fields) => scope === 'ALL' || (scope === 'OWN' && isOwn(record, fields))
    || (scope === 'TEAM' && (isOwn(record, fields) || isTeams(record, fields)))
  return {
    ...p,
    canView: p.view !== 'NONE',
    canCreate: !!p.create,
    field: (key) => p.fields?.[key] || 'EDIT',
    canEdit: (record, fields) => allows(p.edit, record, fields),
    canDelete: (record, fields) => allows(p.delete, record, fields),
  }
}

export const useEntityAccess = (entityType) => {
  const { data } = usePermissions()
  return accessFor(data, entityType)
}

/** A published form as the member's role sees it: hidden fields removed, read-only ones locked. */
export const applyFieldAccess = (form, perms) => {
  if (!form || !perms || perms.admin) return form
  const access = accessFor(perms, form.entityType)
  if (!Object.keys(access.fields || {}).length) return form
  return {
    ...form,
    sections: form.sections.map((s) => ({
      ...s,
      fields: s.fields
        .filter((f) => access.field(f.fieldKey) !== 'HIDDEN')
        .map((f) => (access.field(f.fieldKey) === 'READ_ONLY' ? { ...f, readOnly: true } : f)),
    })),
  }
}
